import { computed, ref, watch } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { deepCopy } from "@utils/toolBox";
import { captureEvent } from "@/lib/analytics";
import { normalizeTagName } from "@utils/assetTags";
import { selectionForClaude } from "@utils/media";
import { copyText } from "@utils/clipboard";
import type { AssetTag, MMediumUpload } from "@/types";

export type AssetLibraryMode = "picker" | "manage";
export type LibraryMembership = "in" | "out";
export type LibraryView = "in" | "untagged" | "duplicates" | "out";
export type LibrarySort = "most_recent" | "most_used" | "a_z" | "relevance";
export type TagsMode = "all" | "any";
export type MediaTypeFilter = "all" | "image" | "video";
export type GridDensity = "compact" | "comfortable" | "large";

export interface AssetLibraryFilters {
  search: string;
  tags: string[];
  sortBy: LibrarySort;
  view: LibraryView;
  tagsMode: TagsMode;
  mediaType: MediaTypeFilter;
}

interface LibraryCounts {
  in_library: number;
  out_of_library: number;
  untagged: number;
  duplicates: number;
}

interface AssetDimensions {
  width: number;
  height: number;
}

interface LibrarySearchResponse {
  m_medium_uploads: MMediumUpload[];
  total_count: number;
}

interface CreateAssetParams {
  url: string;
  name: string;
  mediaType: MMediumUpload["media_type"];
  placement?: string | null;
  campaign?: { id: number; token: string } | null;
  metadata?: Partial<Pick<MMediumUpload, "width" | "height" | "byte_size" | "content_type">>;
}

const PAGE_SIZE = 30;
const DENSITY_STORAGE_KEY = "media-library-density";

const defaultFilters = (mode: AssetLibraryMode): AssetLibraryFilters => ({
  search: "",
  tags: [],
  sortBy: "most_recent",
  view: "in",
  tagsMode: mode === "manage" ? "all" : "any",
  mediaType: mode === "manage" ? "all" : "image",
});

const readStoredDensity = (): GridDensity => {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    return stored === "compact" || stored === "large" ? stored : "comfortable";
  } catch {
    return "comfortable";
  }
};

// Decodes the file directly: a blob: URL in an <img> is blocked by the img-src CSP of deployed environments.
const readImageDimensions = async (file: File): Promise<AssetDimensions | null> => {
  if (!file.type.startsWith("image/")) return null;
  try {
    const bitmap = await createImageBitmap(file);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
  } catch {
    return null;
  }
};

export const useMAssetLibraryStore = defineStore("mAssetLibrary", () => {
  const mode = ref<AssetLibraryMode>("picker");

  const assets = ref<MMediumUpload[]>([]);
  const allAssetTags = ref<AssetTag[]>([]);
  const counts = ref<LibraryCounts>({ in_library: 0, out_of_library: 0, untagged: 0, duplicates: 0 });
  // Images per tag among the assets matching the current filters (manage mode only).
  const tagCounts = ref<Record<string, number> | null>(null);

  const searchQuery = ref("");
  const activeTagFilters = ref<string[]>([]);
  const sortBy = ref<LibrarySort>("most_recent");
  const view = ref<LibraryView>("in");
  const tagsMode = ref<TagsMode>("any");
  const mediaType = ref<MediaTypeFilter>("image");

  const membership = computed<LibraryMembership>(() => (view.value === "out" ? "out" : "in"));

  const page = ref(1);
  const totalCount = ref(0);
  const hasMore = computed(() => assets.value.length < totalCount.value);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const isLoadingTags = ref(false);

  const filters = computed<AssetLibraryFilters>(() => ({
    search: searchQuery.value,
    tags: [...activeTagFilters.value],
    sortBy: sortBy.value,
    view: view.value,
    tagsMode: tagsMode.value,
    mediaType: mediaType.value,
  }));

  const applyFilters = (next: Partial<AssetLibraryFilters>) => {
    const defined = Object.fromEntries(Object.entries(next).filter(([, value]) => value !== undefined));
    const merged = { ...defaultFilters(mode.value), ...defined };
    searchQuery.value = merged.search;
    activeTagFilters.value = [...merged.tags];
    sortBy.value = merged.sortBy;
    view.value = merged.view;
    tagsMode.value = merged.tagsMode;
    mediaType.value = merged.mediaType;
  };

  const density = ref<GridDensity>(readStoredDensity());
  watch(density, (value) => {
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, value);
    } catch {
      // The density is a convenience: losing it only resets the grid to its default.
    }
  });

  // Selection

  const selectedIds = ref<number[]>([]);

  const isSelected = (id: number) => selectedIds.value.includes(id);

  const toggleSelection = (id: number) => {
    const index = selectedIds.value.indexOf(id);
    if (index === -1) selectedIds.value.push(id);
    else selectedIds.value.splice(index, 1);
  };

  const clearSelection = () => {
    selectedIds.value = [];
  };

  const deselect = (ids: number[]) => {
    selectedIds.value = selectedIds.value.filter((id) => !ids.includes(id));
  };

  // Every asset seen in this session, so the selection rail can render a selected
  // asset even after a new search dropped it from the visible page.
  const knownAssets = ref(new Map<number, MMediumUpload>());
  const remember = (list: MMediumUpload[]) => list.forEach((asset) => knownAssets.value.set(asset.id, asset));

  const selectedAssets = computed(() =>
    selectedIds.value.map((id) => knownAssets.value.get(id)).filter((asset): asset is MMediumUpload => !!asset),
  );

  // Dimensions: stored at upload time, or measured once the browser has loaded the image.

  const measuredDimensions = ref(new Map<number, AssetDimensions>());

  const recordDimensions = (id: number, dimensions: AssetDimensions) => {
    if (!measuredDimensions.value.has(id)) measuredDimensions.value.set(id, dimensions);
  };

  const dimensionsOf = (asset: MMediumUpload): AssetDimensions | null => {
    if (asset.width && asset.height) return { width: asset.width, height: asset.height };
    return measuredDimensions.value.get(asset.id) ?? null;
  };

  // Placements the picked images will be shown in (the creative's, in picker mode).
  const targetPlacements = ref<string[]>([]);

  // The campaign the picker serves: its survey ranks images and tags by relevance.
  const campaignToken = ref<string | null>(null);

  // Images the caller already uses (the creative's, in picker mode), flagged "In use" on their card.
  const inUseIds = ref<number[]>([]);

  // Loading

  const filtersFormData = () => {
    const formData = new FormData();
    formData.append("asset_tags", activeTagFilters.value.join(","));
    formData.append("tags_mode", tagsMode.value);
    formData.append("search", searchQuery.value);
    formData.append("library", membership.value);
    if (view.value === "untagged") formData.append("untagged", "true");
    if (view.value === "duplicates") formData.append("duplicates", "true");
    if (mediaType.value !== "all") formData.append("media_type", mediaType.value);
    return formData;
  };

  const requestAssets = async (requestedPage: number, perPage: number) => {
    try {
      const formData = filtersFormData();
      formData.append("page", String(requestedPage));
      formData.append("per_page", String(perPage));
      formData.append("sort_by", sortBy.value);
      if (sortBy.value === "relevance" && campaignToken.value) formData.append("m_campaign_token", campaignToken.value);

      const { response, data } = await useMyFetch("/m_medium_uploads/search").post(formData).json<LibrarySearchResponse>();
      if (!response.value?.ok || !data.value) return null;

      const { m_medium_uploads: uploads, total_count } = data.value;
      remember(uploads);
      totalCount.value = total_count;
      return deepCopy(uploads);
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  const fetchPage = async () => {
    const list = await requestAssets(page.value, PAGE_SIZE);
    if (!list) return;
    assets.value = page.value === 1 ? list : [...assets.value, ...list];
  };

  const reloadLoadedPages = async () => {
    const list = await requestAssets(1, page.value * PAGE_SIZE);
    if (list) assets.value = list;
  };

  const fetchTagCounts = async () => {
    if (mode.value !== "manage") return;
    try {
      const { response, data } = await useMyFetch("/m_medium_uploads/tag_counts")
        .post(filtersFormData())
        .json<Record<string, number>>();
      if (response.value?.ok && data.value) tagCounts.value = data.value;
    } catch (err) {
      handleError(err);
    }
  };

  const search = async () => {
    page.value = 1;
    isLoading.value = true;
    await Promise.all([fetchPage(), fetchTagCounts()]);
    isLoading.value = false;
  };

  const fetchMore = async () => {
    if (isLoadingMore.value || !hasMore.value) return;
    isLoadingMore.value = true;
    page.value += 1;
    try {
      await fetchPage();
    } finally {
      isLoadingMore.value = false;
    }
  };

  const fetchTags = async () => {
    isLoadingTags.value = true;
    try {
      const query = campaignToken.value ? `?m_campaign_token=${encodeURIComponent(campaignToken.value)}` : "";
      const { response, data } = await useMyFetch(`/asset_tags${query}`).json<AssetTag[]>();
      if (response.value?.ok && data.value) allAssetTags.value = data.value;
    } catch (err) {
      handleError(err);
    } finally {
      isLoadingTags.value = false;
    }
  };

  const fetchCounts = async () => {
    try {
      const { response, data } = await useMyFetch("/m_medium_uploads/counts").json<LibraryCounts>();
      if (response.value?.ok && data.value) counts.value = data.value;
    } catch (err) {
      handleError(err);
    }
  };

  const refreshManageData = () =>
    mode.value === "manage" ? Promise.all([fetchCounts(), fetchTags(), fetchTagCounts()]) : null;

  const open = async (
    nextMode: AssetLibraryMode,
    {
      selection = [],
      inUse = [],
      placements = [],
      campaign = null,
      initialFilters = {},
    }: {
      selection?: number[];
      inUse?: number[];
      placements?: string[];
      campaign?: string | null;
      initialFilters?: Partial<AssetLibraryFilters>;
    } = {},
  ) => {
    mode.value = nextMode;
    selectedIds.value = [...selection];
    inUseIds.value = [...inUse];
    targetPlacements.value = [...placements];
    campaignToken.value = campaign;
    editedAsset.value = null;
    lightboxAssetId.value = null;
    applyFilters(nextMode === "picker" ? { sortBy: campaign ? "relevance" : undefined } : initialFilters);
    isLoading.value = true;
    page.value = 1;
    tagCounts.value = null;
    await Promise.all([fetchPage(), refreshManageData() ?? fetchTags()]);
    isLoading.value = false;
  };

  let searchDebounce: ReturnType<typeof setTimeout> | undefined;
  const setSearchQuery = (value: string) => {
    searchQuery.value = value;
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(search, 300);
  };

  const setSortBy = (value: LibrarySort) => {
    sortBy.value = value;
    search();
  };

  const setTagsMode = (value: TagsMode) => {
    if (tagsMode.value === value) return;
    tagsMode.value = value;
    if (activeTagFilters.value.length > 1) search();
  };

  const setMediaType = (value: MediaTypeFilter) => {
    if (mediaType.value === value) return;
    mediaType.value = value;
    search();
  };

  const toggleTagFilter = (tagName: string) => {
    const index = activeTagFilters.value.indexOf(tagName);
    if (index === -1) activeTagFilters.value.push(tagName);
    else activeTagFilters.value.splice(index, 1);
    if (view.value === "untagged") view.value = "in";
    search();
  };

  const clearTagFilters = () => {
    activeTagFilters.value = [];
    search();
  };

  const setView = (next: LibraryView) => {
    if (view.value === next) return;
    view.value = next;
    if (next === "untagged") activeTagFilters.value = [];
    clearSelection();
    search();
  };

  // Edit panel

  const editedAsset = ref<MMediumUpload | null>(null);
  const isSavingEditedAsset = ref(false);

  const setEditedAsset = (asset: MMediumUpload | null) => {
    editedAsset.value = asset ? deepCopy(asset) : null;
  };

  const hasMissingName = (asset: MMediumUpload) => !asset.name?.trim();
  const hasMissingTags = (asset: MMediumUpload) => !asset.asset_tags?.length;

  const replaceAsset = (updated: Pick<MMediumUpload, "id" | "name" | "asset_tags">) => {
    const patch = (asset: MMediumUpload | undefined) => {
      if (!asset) return;
      asset.name = updated.name;
      asset.asset_tags = updated.asset_tags;
    };
    patch(assets.value.find((asset) => asset.id === updated.id));
    patch(knownAssets.value.get(updated.id));
  };

  const updateAsset = async (asset: MMediumUpload, { refresh = true }: { refresh?: boolean } = {}) => {
    if (hasMissingName(asset) || hasMissingTags(asset)) return null;
    isSavingEditedAsset.value = true;
    try {
      const formData = new FormData();
      formData.append("m_medium_upload_id", String(asset.id));
      formData.append("m_medium_upload_name", asset.name);
      formData.append("m_medium_upload_asset_tags", (asset.asset_tags ?? []).join(","));

      const { response, data } = await useMyFetch("/m_medium_uploads/update_tags")
        .post(formData)
        .json<{ all_asset_tags: AssetTag[]; m_medium_upload: MMediumUpload }>();
      if (!response.value?.ok || !data.value) return null;

      captureEvent("media_tags_updated", { m_medium_upload_id: asset.id });
      allAssetTags.value = data.value.all_asset_tags;
      replaceAsset(data.value.m_medium_upload);
      if (refresh) await refreshManageData();
      return data.value.m_medium_upload;
    } catch (err) {
      handleError(err);
      return null;
    } finally {
      isSavingEditedAsset.value = false;
    }
  };

  const bulkUpdateTags = async (ids: number[], { add = [], remove = [] }: { add?: string[]; remove?: string[] }) => {
    try {
      const { response } = await useMyFetch("/m_medium_uploads/bulk_update_tags")
        .post({ m_medium_upload_ids: ids, add_tags: add, remove_tags: remove })
        .text();
      if (!response.value?.ok) return false;

      captureEvent("media_bulk_tagged", { media_count: ids.length });
      await Promise.all([reloadLoadedPages(), refreshManageData() ?? fetchTags()]);
      return true;
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  const suggestTags = async (id: number) => {
    try {
      const { response, data } = await useMyFetch("/m_medium_uploads/suggest_tags")
        .post({ m_medium_upload_id: id })
        .json<{ tags: string[] }>();
      if (!response.value?.ok || !data.value) return [];

      captureEvent("media_tags_suggested", { suggested_count: data.value.tags.length });
      return data.value.tags;
    } catch (err) {
      handleError(err);
      return [];
    }
  };

  const copySelectionForClaude = async () => {
    const assetsToCopy = selectedAssets.value;
    if (!assetsToCopy.length) return false;
    try {
      await copyText(selectionForClaude(assetsToCopy));
      captureEvent("media_selection_copied", { media_count: assetsToCopy.length });
      return true;
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  // Tag hygiene

  const renameTag = async (tag: AssetTag, name: string) => {
    const normalized = normalizeTagName(name);
    if (!normalized || normalized === tag.name) return false;
    const merged = allAssetTags.value.some((other) => other.id !== tag.id && other.name === normalized);

    try {
      const { response } = await useMyFetch(`/asset_tags/${tag.id}`).patch({ asset_tag: { name: normalized } });
      if (!response.value?.ok) return false;

      captureEvent("asset_tag_renamed", { merged });
      activeTagFilters.value = [...new Set(activeTagFilters.value.map((active) => (active === tag.name ? normalized : active)))];
      await Promise.all([reloadLoadedPages(), refreshManageData() ?? fetchTags()]);
      return true;
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  // Library membership

  const postMembership = async (endpoint: "hide_from_library" | "restore_to_library", id: number) => {
    const formData = new FormData();
    formData.append("m_medium_upload_id", String(id));
    const { response } = await useMyFetch(`/m_medium_uploads/${endpoint}`).post(formData).json<MMediumUpload>();
    return !!response.value?.ok;
  };

  const moveAssets = async (ids: number[], endpoint: "hide_from_library" | "restore_to_library") => {
    try {
      const results = await Promise.all(ids.map((id) => postMembership(endpoint, id)));
      const movedIds = ids.filter((_, index) => results[index]);

      deselect(movedIds);
      if (editedAsset.value && movedIds.includes(editedAsset.value.id)) editedAsset.value = null;
      await Promise.all([reloadLoadedPages(), refreshManageData()]);

      return results.every(Boolean);
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  const hideFromLibrary = async (ids: number[]) => {
    const succeeded = await moveAssets(ids, "hide_from_library");
    if (succeeded) captureEvent("media_hidden_from_library", { media_count: ids.length });
    return succeeded;
  };

  const restoreToLibrary = async (ids: number[]) => {
    const succeeded = await moveAssets(ids, "restore_to_library");
    if (succeeded) captureEvent("media_restored_to_library", { media_count: ids.length });
    return succeeded;
  };

  // Lightbox

  const lightboxAssetId = ref<number | null>(null);

  const lightboxIndex = computed(() =>
    lightboxAssetId.value === null ? -1 : assets.value.findIndex((asset) => asset.id === lightboxAssetId.value),
  );
  const lightboxAsset = computed(() => assets.value[lightboxIndex.value] ?? null);

  const openLightbox = (id: number) => {
    lightboxAssetId.value = id;
  };

  const closeLightbox = () => {
    lightboxAssetId.value = null;
  };

  const stepLightbox = async (offset: 1 | -1) => {
    const nextIndex = lightboxIndex.value + offset;
    if (nextIndex >= assets.value.length && hasMore.value) await fetchMore();
    const next = assets.value[nextIndex];
    if (next) lightboxAssetId.value = next.id;
  };

  // Moves the lightbox to a neighbour before its asset leaves the list (taken out, put back), so it
  // never points at a missing asset while the list reloads — that would close and reopen the dialog.
  const takeLightboxAssetOut = async (move: (ids: number[]) => Promise<boolean>) => {
    const current = lightboxAsset.value;
    if (!current) return null;
    const index = lightboxIndex.value;
    lightboxAssetId.value = (assets.value[index + 1] ?? assets.value[index - 1])?.id ?? null;

    const succeeded = await move([current.id]);
    if (!succeeded) {
      lightboxAssetId.value = current.id;
      return null;
    }

    // The neighbour can leave the list too: the last copy of a duplicate is no longer one.
    if (lightboxIndex.value === -1) lightboxAssetId.value = assets.value[Math.min(index, assets.value.length - 1)]?.id ?? null;
    return current;
  };

  // Upload

  const uploadFile = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file[attachment]", file);
      formData.append("file[type]", file.type);

      const { data } = await useMyFetch("/file_uploads/").post(formData).json<{ url: string }>();
      return data.value;
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  const createAsset = async ({
    url,
    name,
    mediaType: assetMediaType,
    placement = null,
    campaign = null,
    metadata = {},
  }: CreateAssetParams) => {
    try {
      const body = {
        m_campaign_id: campaign?.id,
        m_campaign_token: campaign?.token,
        media_type: assetMediaType,
        placement,
        url,
        name: name.replace(/\.[^/.]+$/, ""),
        ...metadata,
      };
      const { response, data } = await useMyFetch("/m_medium_uploads").post(body).json<MMediumUpload>();
      if (!response.value?.ok || !data.value) return null;

      captureEvent("media_uploaded", { m_campaign_token: campaign?.token, media_type: assetMediaType });
      return data.value;
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  const fileMetadata = async (file: File) => {
    const dimensions = await readImageDimensions(file);
    return { byte_size: file.size, content_type: file.type || null, ...(dimensions ?? {}) };
  };

  const uploadAssets = async (files: File[], tags: string[]) => {
    const uploaded: MMediumUpload[] = [];
    for (const file of files) {
      const [hosted, metadata] = await Promise.all([uploadFile(file), fileMetadata(file)]);
      if (!hosted?.url) continue;

      const created = await createAsset({
        url: hosted.url,
        name: file.name,
        mediaType: file.type.startsWith("video/") ? "video" : "image",
        metadata,
      });
      if (!created) continue;

      const tagged = tags.length ? await updateAsset({ ...created, asset_tags: [...tags] }, { refresh: false }) : null;
      uploaded.push({ ...created, asset_tags: tagged?.asset_tags ?? created.asset_tags ?? [] });
    }

    remember(uploaded);
    if (mode.value === "picker") uploaded.forEach((asset) => !isSelected(asset.id) && selectedIds.value.push(asset.id));
    await Promise.all([reloadLoadedPages(), refreshManageData()]);
    return uploaded;
  };

  return {
    mode,
    assets,
    allAssetTags,
    tagCounts,
    counts,
    searchQuery,
    activeTagFilters,
    sortBy,
    view,
    tagsMode,
    mediaType,
    membership,
    filters,
    density,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    isLoadingTags,
    inUseIds,
    targetPlacements,
    campaignToken,
    open,
    search,
    setSearchQuery,
    setSortBy,
    setTagsMode,
    setMediaType,
    fetchMore,
    fetchTags,
    toggleTagFilter,
    clearTagFilters,
    setView,

    selectedIds,
    selectedAssets,
    isSelected,
    toggleSelection,
    clearSelection,

    recordDimensions,
    dimensionsOf,

    editedAsset,
    isSavingEditedAsset,
    setEditedAsset,
    hasMissingName,
    hasMissingTags,
    updateAsset,
    bulkUpdateTags,
    suggestTags,
    refreshManageData,
    copySelectionForClaude,
    renameTag,

    hideFromLibrary,
    restoreToLibrary,

    lightboxAsset,
    lightboxIndex,
    openLightbox,
    closeLightbox,
    stepLightbox,
    takeLightboxAssetOut,

    uploadFile,
    createAsset,
    uploadAssets,
  };
});
